/// VariableInspector: Live-updating panel for inspecting process instance variables.
/// Shows an instance selector dropdown, a refreshable collapsible tree view of
/// variables, and auto-updates when engine events arrive for the selected instance.

import { useState, useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { useEngineStore } from "@/stores/engineStore";
import { useVariableInspector } from "@/hooks/useVariableInspector";
import { VariableTree } from "./variables/VariableTree";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VariableInspector() {
  const runningInstances = useEngineStore((s) => s.runningInstances);
  const [selectedInstanceKey, setSelectedInstanceKey] = useState<string | null>(null);
  const { scope, isLoading, error, refresh } = useVariableInspector(selectedInstanceKey);

  // Auto-select first active instance when none is selected
  useEffect(() => {
    if (!selectedInstanceKey) {
      const active = runningInstances.find((i) => i.state === "running");
      if (active) setSelectedInstanceKey(active.instanceKey);
    }
  }, [runningInstances, selectedInstanceKey]);

  return (
    <div className="flex flex-col h-full">
      {/* Instance selector */}
      <div className="flex items-center gap-2 border-b border-zinc-700 px-3 py-1.5 shrink-0">
        <select
          value={selectedInstanceKey ?? ""}
          onChange={(e) => setSelectedInstanceKey(e.target.value || null)}
          className="flex-1 bg-zinc-800 text-xs text-zinc-200 rounded px-2 py-1 focus:outline-none border border-zinc-600"
        >
          <option value="">Select instance...</option>
          {runningInstances.map((inst) => (
            <option key={inst.instanceKey} value={inst.instanceKey}>
              {inst.instanceKey.slice(0, 8)}... - {inst.processId} ({inst.state})
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => refresh()}
          disabled={isLoading || !selectedInstanceKey}
          className="text-zinc-500 hover:text-zinc-300 disabled:opacity-30 transition-colors"
          aria-label="Refresh variables"
        >
          <RefreshCw size={12} className={isLoading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Error display */}
      {error && (
        <div className="px-3 py-1.5 text-xs text-red-400 bg-red-950/30 border-b border-zinc-700 shrink-0">
          {error}
        </div>
      )}

      {/* Variable tree */}
      <div className="flex-1 overflow-y-auto min-h-0">
        {scope ? (
          <VariableTree data={scope.variables} />
        ) : (
          <div className="flex items-center justify-center h-full text-xs text-zinc-500">
            {selectedInstanceKey
              ? isLoading
                ? "Loading..."
                : "No variables available"
              : "Select an instance to inspect variables"}
          </div>
        )}
      </div>

      {/* Status footer */}
      {scope && (
        <div className="border-t border-zinc-700 px-3 py-0.5 text-[10px] text-zinc-500 shrink-0">
          {Object.keys(scope.variables).length} variables | Updated{" "}
          {new Date(scope.lastUpdated).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}
